import React, { useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import CustomModal from "../../basics/interface/CustomModal"
import { COLORS } from "../../../utilities/Constants"
import { DEP_STATES } from "../../../utilities/Addons"
import {
	appliquerProposition,
	rejeterProposition,
} from "../../../store/actions/assistancesActions"
import { useAssistances } from "./AssistancesContext"
import {
	BandeauOmbre,
	LIBELLES_CHAMPS,
	RoleChip,
	VTag,
	dateCourte,
	ui,
} from "./assistancesUi"
import { NIVEAU, Raison, chf } from "../couvertures/couverturesUi"
import SelecteurCibleModal from "../couvertures/SelecteurCibleModal"
import PointInteretSelectV2, {
	POINTS_INTERETS_V2,
	POINTS_INTERETS_V2_ATELIER,
} from "../../others/PointInteretSelectV2"
import { adresseDepuisEmail } from "../../../utilities/adresseDepuisEmail"
import { isPoValid } from "../../../utilities/poiValide"
import {
	CarteMissionDepliee,
	ChevronMission,
	useDepliage,
} from "./MissionDepliable"

// Modal Proposition (GE-57, spec §11.4) en deux rendus :
// - différences de champs (Champ / Mission actuelle / Ordre vN / Appliquer)
//   quand la proposition (ou le choix retenu) met à jour des champs ;
// - structurel AVANT | APRÈS sinon (doublon, annulation, transport sans
//   dépose…), avec les alternatives en boutons radio.
// Rejeter → bascule sur le bouton (motif obligatoire), pas de second modal.
// Erreurs (dont « périmée » 409) affichées DANS le modal.

const valeurAffichee = (champ, v) => {
	if (v === null || v === undefined || v === "" || v === 0) return null
	if (champ === "total_presume") return `${Number(v).toFixed(2)}`
	return String(v)
}

// Neutralise le CSS global des tables (App.css : bordures grises, centrage,
// zébrage) — mémoire projet « CSS global legacy sur les tables ».
const cellule = {
	border: 0,
	textAlign: "left",
	verticalAlign: "top",
	backgroundColor: "transparent",
	padding: "10px 10px 10px 0",
	borderBottom: `1px solid ${COLORS.border}`,
	fontSize: 13,
	color: COLORS.text,
}
const entete = {
	...cellule,
	fontSize: 11,
	fontWeight: 600,
	letterSpacing: "0.08em",
	textTransform: "uppercase",
	color: COLORS.textLight,
	padding: "0 10px 8px 0",
}

// « Reçu le 23.09 08:21 · mission M862 · « Dispatché » à J. P. » (maquette).
const sousTitre = (p) => {
	const m = p.contexte && p.contexte.mission
	return [
		`Reçu le ${dateCourte(p.cree_le)}`,
		m ? `mission M${m.id}` : null,
		m && m.etat_label
			? `« ${m.etat_label} »${m.depanneur ? ` à ${m.depanneur}` : ""}`
			: null,
		p.simulee ? "simulée (mode ombre)" : null,
	]
		.filter(Boolean)
		.join(" · ")
}

const TableDiff = ({ ops, coches, basculer, version, identiques = [] }) => (
	<table
		style={{
			width: "100%",
			borderCollapse: "collapse",
			// App.css met user-select: none sur les tables.
			userSelect: "text",
		}}
	>
		<thead style={{ backgroundColor: "transparent" }}>
			<tr style={{ backgroundColor: "transparent" }}>
				<th style={entete}>Champ</th>
				<th style={entete}>Mission actuelle</th>
				<th style={entete}>Ordre{version ? ` v${version}` : ""}</th>
				<th style={{ ...entete, textAlign: "center" }}>Appliquer</th>
			</tr>
		</thead>
		<tbody>
			{ops.map((op) => {
				const [champ, [avant, apres]] = Object.entries(op.champs)[0]
				const ancien = valeurAffichee(champ, avant)
				return (
					<tr
						key={`op_${op.index}`}
						style={{ backgroundColor: "transparent" }}
					>
						<td
							style={{
								...cellule,
								color: COLORS.textMuted,
								fontWeight: 500,
								whiteSpace: "nowrap",
							}}
						>
							{LIBELLES_CHAMPS[champ] || champ}
						</td>
						<td style={cellule}>
							{ancien ? (
								<span
									style={{
										color: COLORS.textMuted,
										textDecoration: "line-through",
										textDecorationColor:
											COLORS.borderStrong,
									}}
								>
									{ancien}
								</span>
							) : (
								<span style={{ color: COLORS.textLight }}>
									—
								</span>
							)}
						</td>
						<td style={cellule}>
							<span
								style={{
									fontWeight: 600,
									backgroundColor: COLORS.successLight,
									borderRadius: 4,
									padding: "1px 5px",
								}}
							>
								{valeurAffichee(champ, apres)}
							</span>
						</td>
						<td style={{ ...cellule, textAlign: "center" }}>
							<input
								type="checkbox"
								checked={coches.includes(op.index)}
								onChange={() => basculer(op.index)}
								style={{
									width: 17,
									height: 17,
									accentColor: COLORS.text,
								}}
								aria-label={`Appliquer ${LIBELLES_CHAMPS[champ] || champ}`}
							/>
						</td>
					</tr>
				)
			})}
			{identiques.map((i) => (
				<tr
					key={`id_${i.champ}`}
					style={{ backgroundColor: "transparent" }}
				>
					<td
						style={{
							...cellule,
							color: COLORS.textMuted,
							fontWeight: 500,
							whiteSpace: "nowrap",
						}}
					>
						{LIBELLES_CHAMPS[i.champ] || i.champ}
					</td>
					<td style={cellule}>{valeurAffichee(i.champ, i.valeur)}</td>
					<td style={{ ...cellule, color: COLORS.textLight }}>
						identique
					</td>
					<td
						style={{
							...cellule,
							textAlign: "center",
							color: COLORS.textLight,
						}}
					>
						—
					</td>
				</tr>
			))}
		</tbody>
	</table>
)

// Mini-arbres AVANT | APRÈS : l'état lu au calcul, puis les opérations du
// choix retenu (vert = ajout / lien, rouge = annulation).
const AvantApres = ({ p, ops, attenteChoix = false }) => {
	const avant = p.avant || {}
	const lignesAvant = []
	if (avant.missions)
		Object.entries(avant.missions).forEach(([id, etat]) =>
			lignesAvant.push({
				texte: `M${id} · ${DEP_STATES[etat] || etat}`,
				mission: parseInt(id),
			}),
		)
	if (avant.mission)
		lignesAvant.push({
			texte: `M${avant.mission}${
				avant.etat !== undefined
					? ` · ${DEP_STATES[avant.etat] || ""}`
					: ""
			}`,
			mission: avant.mission,
		})
	if (avant.destination_mission)
		lignesAvant.push(
			`Destination mission : ${avant.destination_mission.filter(Boolean).join(", ") || "—"}`,
		)
	if (p.apres && p.apres.destination_ordre)
		lignesAvant.push(
			`Destination ordre : ${
				[
					p.apres.destination_ordre.rue,
					p.apres.destination_ordre.npa_localite,
				]
					.filter(Boolean)
					.join(", ") || "—"
			}`,
		)
	if (p.apres && (p.apres.missions_parties || []).length)
		lignesAvant.push(
			`Déjà partie : ${p.apres.missions_parties.map((m) => `M${m}`).join(", ")} — prévenir le dépanneur`,
		)
	if (p.type === "rapprochement" && p.apres)
		lignesAvant.push(
			p.apres.ref_affaire
				? `Dossier ${p.dossier.assistance} ${p.apres.ref_affaire} (même plaque${
						p.apres.plaques ? ` ${p.apres.plaques}` : ""
					})`
				: {
						texte: `M${p.apres.mission} (même plaque${
							p.apres.plaques ? ` ${p.apres.plaques}` : ""
						})`,
						mission: p.apres.mission,
					},
		)
	if (!lignesAvant.length) lignesAvant.push("État actuel du dossier")
	const racine = `📁 ${p.dossier.assistance} ${p.dossier.ref_affaire}`
	return (
		<div style={st.ba}>
			<div style={st.col}>
				<div style={st.colTitre}>Avant</div>
				<div style={st.racine}>{racine}</div>
				{lignesAvant.map((l, i) => (
					<div
						key={`av_${i}`}
						style={st.noeud(i === lignesAvant.length - 1)}
					>
						{typeof l === "string" ? (
							l
						) : (
							<LigneMissionDepliable
								texte={l.texte}
								missionId={l.mission}
							/>
						)}
					</div>
				))}
			</div>
			<div
				style={{
					...st.col,
					borderColor: "#BBF7D0",
					backgroundColor: "#FBFEFB",
				}}
			>
				<div style={st.colTitre}>Après</div>
				<div style={st.racine}>{racine}</div>
				{ops.length === 0 && (
					<div style={st.noeud(true)}>
						{attenteChoix
							? "(choisir une option ci-dessus)"
							: "(aucun changement)"}
					</div>
				)}
				{ops.map((op, i) => (
					<div
						key={`ap_${op.index}`}
						style={st.noeud(i === ops.length - 1)}
					>
						<span
							style={
								op.op === "annuler_mission" ? st.tuer : st.ajout
							}
						>
							{op.libelle}
						</span>
					</div>
				))}
			</div>
		</div>
	)
}

// Ligne « Avant » qui cite une mission : dépliable en sa carte complète.
const LigneMissionDepliable = ({ texte, missionId }) => {
	const [ouvert, basculer] = useDepliage()
	return (
		<>
			<span
				style={{ display: "inline-flex", gap: 6, alignItems: "center" }}
			>
				<ChevronMission
					ouvert={ouvert}
					onClick={basculer}
					missionId={missionId}
				/>
				{texte}
			</span>
			{ouvert && <CarteMissionDepliee missionId={missionId} />}
		</>
	)
}

// GE-60 (C3) : choix « Couverture de la fiche N° … » — niveau et raisons du
// moteur de correspondance (mêmes pastilles que le sélecteur).
const CandidatCouverture = ({ c, argent, assistance }) => {
	const niveau = NIVEAU[c.niveau]
	return (
		<div
			style={{
				display: "flex",
				flexWrap: "wrap",
				gap: 6,
				marginTop: 4,
				alignItems: "center",
				fontWeight: 400,
			}}
		>
			{niveau && (
				<span
					style={{
						fontSize: 11,
						fontWeight: 700,
						borderRadius: 99,
						padding: "2px 9px",
						color: niveau.color,
						backgroundColor: niveau.bg,
					}}
				>
					{c.niveau_label}
				</span>
			)}
			<span style={{ fontSize: 12.5, color: COLORS.textMuted }}>
				{[c.date, c.plaques, c.client, c.etat]
					.filter(Boolean)
					.join(" · ")}
			</span>
			<div
				style={{
					display: "flex",
					flexWrap: "wrap",
					gap: 6,
					width: "100%",
				}}
			>
				{(c.raisons || []).map((r, i) => (
					<Raison key={`r_${i}`} r={r} />
				))}
			</div>
			{argent && <ArgentCandidat a={argent} assistance={assistance} />}
		</div>
	)
}

// Point 09.10 : l'argent du candidat (calculé par le serveur à la lecture) —
// plafond vs total, déjà encaissé, simulation, couvertures présentes. Un
// total inconnu n'est jamais affiché « 0.– ».
const ArgentCandidat = ({ a, assistance }) => {
	const devise = a.ordre_devise && a.ordre_devise !== "CHF"
	const objet = a.cible === "fiche" ? "fiche" : "mission"
	const plafond =
		a.plafond !== null
			? `${assistance} couvre ${chf(a.plafond)}`
			: devise && a.ordre_montant !== null
				? `${assistance} : ${a.ordre_montant} ${a.ordre_devise} (devise, plafond à saisir)`
				: `${assistance} : plafond non précisé`
	const total =
		a.total !== null
			? `${objet} ${chf(a.total)}`
			: `total de la ${objet} pas encore calculé`
	const encaisse =
		a.encaisse_client || a.encaisse_assistance
			? `Déjà encaissé : client ${chf(a.encaisse_client)} · assistance ${chf(
					a.encaisse_assistance,
				)}`
			: "Rien d'encaissé"
	let simulation = null
	if (a.rattacher)
		simulation = `L'ordre suit la couverture existante #${a.rattacher} : aucun montant ajouté`
	else if (a.confirmation)
		simulation = `Couverture ${assistance} déjà présente : elle sera confirmée, pas doublée`
	else if (a.simulation && a.simulation.assistance === null)
		simulation = "Si choisi : montant à saisir au bureau"
	else if (a.simulation)
		simulation = `Si choisi : ${assistance} prendrait ${chf(
			a.simulation.assistance,
		)} · reste client ${chf(a.simulation.reste_client)}`
	const payee = a.total !== null && a.reste_a_payer === 0
	const doublee =
		!a.rattacher &&
		!a.confirmation &&
		(a.couvertures || []).some((c) => c.meme_assistance)
	const alertes = [
		a.close &&
			`⚠ ${objet === "fiche" ? "Fiche clôturée" : "Mission annulée"}`,
		!a.close && payee && "⚠ Déjà entièrement payée",
		doublee &&
			`⚠ Une couverture ${assistance} existe déjà ici : elle serait doublée`,
	].filter(Boolean)
	return (
		<div
			style={{
				width: "100%",
				marginTop: 2,
				padding: "6px 10px",
				borderRadius: 8,
				border: `1px solid ${COLORS.border}`,
				backgroundColor: COLORS.surface,
				fontSize: 12.5,
				lineHeight: 1.55,
				color: COLORS.text,
			}}
		>
			<div>
				<b style={{ fontWeight: 600 }}>{plafond}</b>
				<span style={{ color: COLORS.textMuted }}> · {total}</span>
			</div>
			<div style={{ color: COLORS.textMuted }}>{encaisse}</div>
			{simulation && <div style={{ fontWeight: 600 }}>{simulation}</div>}
			{(a.couvertures || []).map((c) => (
				<div key={`cv_${c.id}`} style={{ color: COLORS.textMuted }}>
					Déjà couverte : {c.assistance}{" "}
					{c.plafond !== null
						? chf(c.plafond)
						: "(plafond non précisé)"}
					{c.utilise ? ` · utilisé ${chf(c.utilise)}` : ""}
					{c.statut === "proposee" ? " · à valider" : ""}
				</div>
			))}
			{alertes.map((t) => (
				<div key={t} style={{ color: "#92400E", fontWeight: 600 }}>
					{t}
				</div>
			))}
		</div>
	)
}

// GE-62 (revue phase B, décision 1a) : adresse d'un e-mail AVANT le
// dispatch — jamais reportée en texte brut ; le bureau la formalise en POI
// (« Utiliser cette adresse », pré-rempli et corrigeable), posé à
// l'application.
const AdresseAFormaliser = ({ op, poi, setPoi, coche, basculer }) => {
	const dispatch = useDispatch()
	const [signalNpa, setSignalNpa] = useState(0)
	const quoi =
		op.groupe === "lieu_intervention"
			? "Lieu d'intervention"
			: "Destination"
	const texte = (op.texte || []).filter(Boolean).join(" ")
	const utiliser = () =>
		adresseDepuisEmail(dispatch, op.texte || []).then(
			({ poi: neuf, plusieurs }) => {
				setPoi(neuf)
				if (plusieurs) setSignalNpa((n) => n + 1)
			},
		)
	return (
		<div style={st.adresse}>
			<label
				style={{
					display: "flex",
					gap: 8,
					alignItems: "center",
					fontSize: 12.5,
					fontWeight: 600,
				}}
			>
				{/* Revue 5 (🟠8) : décochée = écartée (acquittée), comme un
				    champ. */}
				<input type="checkbox" checked={coche} onChange={basculer} />
				{quoi} lu dans l'e-mail : {texte || "—"}
			</label>
			<div style={{ fontSize: 12, color: COLORS.textMuted }}>
				Actuellement : {op.actuel || "—"}
			</div>
			{!coche ? null : poi ? (
				<PointInteretSelectV2
					pointInteret={poi}
					list={
						op.groupe === "lieu_intervention"
							? POINTS_INTERETS_V2
							: POINTS_INTERETS_V2_ATELIER
					}
					updatePointInteret={(cle, valeur, entier = false) =>
						setPoi(entier ? valeur : { ...poi, [cle]: valeur })
					}
					signalNpa={signalNpa}
				/>
			) : (
				<button type="button" style={ui.lien} onClick={utiliser}>
					Utiliser cette adresse
				</button>
			)}
		</div>
	)
}

const PropositionModal = ({ proposition: p, onClose }) => {
	const dispatch = useDispatch()
	// Mission du contexte (« … · mission M862 ») dépliable (09.10).
	const [deplieCtx, basculerCtx] = useDepliage()
	const ctx = useAssistances()
	const choix = (p.apres && p.apres.choix) || null
	const clesChoix = useMemo(
		() => [...new Set(p.operations.map((o) => o.choix).filter(Boolean))],
		[p.operations],
	)
	const [choixRetenu, setChoixRetenu] = useState(
		(p.apres && p.apres.defaut) || null,
	)
	const opsDuChoix = p.operations.filter(
		(o) => !o.choix || o.choix === choixRetenu,
	)
	const opsChamps = opsDuChoix.filter((o) => o.op === "maj_mission")
	const [coches, setCoches] = useState(() =>
		p.operations
			.filter((o) => o.op === "maj_mission" || o.op === "adresse_mission")
			.map((o) => o.index),
	)
	const [rejet, setRejet] = useState(false)
	// Jordan 09.10 : « Lier à une autre mission / fiche… » (sélecteur).
	const [selecteur, setSelecteur] = useState(false)
	const [motif, setMotif] = useState("")
	const [erreur, setErreur] = useState(null)
	const [enCours, setEnCours] = useState(false)
	// Décision 1a : POI formalisés par indice d'op `adresse_mission`.
	const [adresses, setAdresses] = useState({})

	const bloquee = p.simulee && !ctx.ombreApplicable
	const retenues = opsDuChoix
		.filter(
			(o) =>
				(o.op !== "maj_mission" && o.op !== "adresse_mission") ||
				coches.includes(o.index),
		)
		.map((o) => o.index)
	const basculer = (i) =>
		setCoches((c) => (c.includes(i) ? c.filter((x) => x !== i) : [...c, i]))

	const fin = (res) => {
		setEnCours(false)
		if (res && res.erreur) {
			setErreur(res.erreur.message)
			// Périmée : le serveur a recalculé, les listes doivent suivre.
			if (res.erreur.httpStatus === 409) ctx.actualiser()
			return
		}
		ctx.actualiser()
		onClose()
	}
	const opsAdresse = opsDuChoix.filter((o) => o.op === "adresse_mission")
	// Revue 5 (🟠5) : formalisée = POI complet (même règle que la fiche
	// mission), pas seulement « un objet existe ».
	const adresseManquante = opsAdresse.some(
		(o) => retenues.includes(o.index) && !isPoValid(adresses[o.index]),
	)
	const appliquer = () => {
		if (adresseManquante) {
			setErreur(
				"Formaliser d'abord l'adresse (« Utiliser cette adresse »).",
			)
			return
		}
		setEnCours(true)
		setErreur(null)
		dispatch(appliquerProposition(p.id, retenues, adresses)).then(fin)
	}
	const rejeter = () => {
		setEnCours(true)
		setErreur(null)
		dispatch(rejeterProposition(p.id, motif.trim())).then(fin)
	}

	const nbChamps = [...opsChamps, ...opsAdresse].filter((o) =>
		coches.includes(o.index),
	).length
	const libelleAppliquer =
		opsChamps.length + opsAdresse.length
			? `Appliquer la sélection (${nbChamps})`
			: "Appliquer"
	const boutons = rejet
		? [
				{
					title: "Annuler",
					styleName: "secondary",
					onClick: () => setRejet(false),
					disabled: enCours,
				},
				{
					title: "Confirmer le rejet",
					styleName: "danger",
					onClick: rejeter,
					disabled: !motif.trim(),
					loading: enCours,
				},
			]
		: [
				{
					title: "Rejeter…",
					styleName: "danger",
					onClick: () => setRejet(true),
					disabled: enCours,
				},
				{
					title: libelleAppliquer,
					styleName: "primary",
					onClick: appliquer,
					disabled:
						bloquee ||
						!retenues.length ||
						(clesChoix.length > 0 && !choixRetenu),
					loading: enCours,
				},
			]
	const piedGauche = rejet ? (
		<input
			autoFocus
			value={motif}
			onChange={(e) => setMotif(e.target.value)}
			placeholder="Motif du rejet (obligatoire)"
			style={st.motif}
		/>
	) : (
		<div style={{ display: "flex", gap: 14, alignItems: "center" }}>
			{(p.emails || []).map((e) => (
				<button
					key={`mail_${e.message_id}`}
					type="button"
					style={ui.lien}
					onClick={() => ctx.ouvrirMessage(e.message_id)}
				>
					Voir l'e-mail{e.label ? ` ${e.label}` : ""}
				</button>
			))}
		</div>
	)

	return (
		<div data-modal-assistance="" data-assistance-ui="">
			<CustomModal
				title={
					<span
						style={{
							display: "flex",
							gap: 10,
							alignItems: "center",
							flexWrap: "wrap",
						}}
					>
						{p.type_label} · {p.dossier.assistance}{" "}
						{(p.contexte && p.contexte.ordre_ref) ||
							p.dossier.ref_affaire}
						{p.contexte && p.contexte.role ? (
							<RoleChip role={p.contexte.role} />
						) : null}
						{p.apres && p.apres.version > 1 ? (
							<VTag>
								v{p.apres.version - 1} → v{p.apres.version}
							</VTag>
						) : null}
					</span>
				}
				subtitle={
					p.contexte && p.contexte.mission ? (
						<span style={st.sousTitre}>
							{sousTitre(p)}
							<ChevronMission
								ouvert={deplieCtx}
								onClick={basculerCtx}
								missionId={p.contexte.mission.id}
							/>
						</span>
					) : (
						sousTitre(p)
					)
				}
				// Requête en cours : pas de fermeture (résultat perdu sinon).
				onClose={enCours ? () => {} : onClose}
				maxWidth={720}
				buttons={boutons}
				footerLeft={piedGauche}
			>
				{deplieCtx && p.contexte && p.contexte.mission && (
					<CarteMissionDepliee missionId={p.contexte.mission.id} />
				)}
				{bloquee && <BandeauOmbre court />}
				{p.mission_partie && (
					<div style={st.parti}>
						⚠ La mission est déjà partie : prévenir le dépanneur de
						ce qui change.
					</div>
				)}
				{p.apres && p.apres.note && (
					<div style={st.parti}>⚠ {p.apres.note}</div>
				)}
				{erreur && <div style={st.erreur}>{erreur}</div>}
				{opsAdresse.map((o) => (
					<AdresseAFormaliser
						key={`adresse_${o.index}`}
						op={o}
						coche={coches.includes(o.index)}
						basculer={() => basculer(o.index)}
						poi={adresses[o.index] || null}
						setPoi={(poi) =>
							setAdresses((a) => ({ ...a, [o.index]: poi }))
						}
					/>
				))}
				{clesChoix.length > 0 && (
					<div style={st.choix}>
						{clesChoix.map((k) => (
							<label
								key={`choix_${k}`}
								style={st.option(choixRetenu === k)}
							>
								<input
									type="radio"
									name={`choix_${p.id}`}
									checked={choixRetenu === k}
									onChange={() => setChoixRetenu(k)}
									style={{
										accentColor: COLORS.text,
										marginTop: 3,
									}}
								/>
								<div>
									<b style={{ fontWeight: 600 }}>
										{(choix && choix[k]) || k}
									</b>
									{p.apres &&
										p.apres.candidats &&
										p.apres.candidats[k] && (
											<CandidatCouverture
												c={p.apres.candidats[k]}
												argent={p.argent && p.argent[k]}
												assistance={
													(p.dossier &&
														p.dossier.assistance) ||
													"L'assistance"
												}
											/>
										)}
									{p.apres && p.apres.defaut === k && (
										<div
											style={{
												color: COLORS.textMuted,
												fontSize: 12.5,
											}}
										>
											Proposé par défaut.
										</div>
									)}
								</div>
							</label>
						))}
					</div>
				)}
				{p.ordre_a_lier && !bloquee && (
					<div style={st.lierMain}>
						Aucun de ces choix ?{" "}
						<button
							type="button"
							style={ui.lien}
							onClick={() => setSelecteur(true)}
						>
							Lier à une autre mission / fiche…
						</button>
					</div>
				)}
				{p.type === "doublon" && (
					<div style={st.aide}>
						Deux missions réellement différentes (ex. véhicule et
						remorque) ? Rejeter avec ce motif : les deux sont
						gardées.
					</div>
				)}
				{opsChamps.length > 0 ? (
					<TableDiff
						ops={opsChamps}
						coches={coches}
						basculer={basculer}
						version={p.apres && p.apres.version}
						identiques={(p.contexte && p.contexte.identiques) || []}
					/>
				) : (
					<AvantApres
						p={p}
						ops={
							clesChoix.length > 0 && !choixRetenu
								? []
								: opsDuChoix
						}
						attenteChoix={clesChoix.length > 0 && !choixRetenu}
					/>
				)}
			</CustomModal>
			{selecteur && (
				<SelecteurCibleModal
					mode="ordre"
					ordreId={p.ordre_a_lier}
					propositionId={p.id}
					titreOrdre={`Lier l'ordre ${
						(p.contexte && p.contexte.ordre_ref) ||
						p.dossier.ref_affaire
					} à…`}
					onClose={() => setSelecteur(false)}
					onDone={() => {
						setSelecteur(false)
						ctx.actualiser()
						onClose()
					}}
				/>
			)}
		</div>
	)
}

const st = {
	adresse: {
		display: "flex",
		flexDirection: "column",
		gap: 8,
		padding: "10px 12px",
		marginBottom: 12,
		borderRadius: 8,
		border: `1px solid ${COLORS.border}`,
		background: COLORS.bg,
	},
	sousTitre: {
		display: "inline-flex",
		gap: 8,
		alignItems: "center",
		flexWrap: "wrap",
	},
	lierMain: {
		fontSize: 12.5,
		color: COLORS.textMuted,
		margin: "-6px 0 14px",
	},
	aide: {
		fontSize: 12.5,
		color: COLORS.textMuted,
		margin: "-6px 0 14px",
	},
	parti: {
		display: "flex",
		gap: 8,
		alignItems: "center",
		fontSize: 13,
		padding: "9px 12px",
		borderRadius: 10,
		backgroundColor: COLORS.warningLight,
		color: "#92400E",
		border: "1px solid #FDE68A",
		marginBottom: 14,
	},
	erreur: {
		fontSize: 13,
		padding: "9px 12px",
		borderRadius: 10,
		backgroundColor: COLORS.dangerLight,
		color: COLORS.danger,
		border: `1px solid ${COLORS.dangerBorder}`,
		marginBottom: 14,
	},
	choix: {
		display: "flex",
		flexDirection: "column",
		gap: 8,
		marginBottom: 14,
	},
	option: (sel) => ({
		display: "flex",
		gap: 10,
		alignItems: "flex-start",
		border: `1px solid ${sel ? COLORS.text : COLORS.border}`,
		borderRadius: 10,
		padding: "10px 12px",
		cursor: "pointer",
		fontSize: 13,
	}),
	ba: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
		gap: 14,
	},
	col: {
		border: `1px solid ${COLORS.border}`,
		borderRadius: 12,
		padding: "12px 14px",
		fontSize: 12.5,
		lineHeight: 1.5,
	},
	colTitre: {
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: "0.1em",
		textTransform: "uppercase",
		color: COLORS.textLight,
		marginBottom: 8,
	},
	racine: { fontWeight: 700, marginBottom: 2 },
	noeud: (dernier) => ({
		padding: "3px 0 3px 14px",
		position: "relative",
		borderLeft: dernier ? "none" : `1px solid ${COLORS.border}`,
		marginLeft: 4,
	}),
	ajout: {
		backgroundColor: COLORS.successLight,
		color: "#166534",
		borderRadius: 4,
		padding: "0 4px",
		fontWeight: 600,
	},
	tuer: { color: COLORS.danger, fontWeight: 600 },
	motif: {
		width: 280,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 8,
		padding: "8px 10px",
		fontFamily: "inherit",
		fontSize: 13,
	},
}

export default PropositionModal
